import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import {
  LandingGeneralContainer,
  ProfileContactWrapper,
  ProfilePageComponentGrid,
  ProfilePageComponentWrapper,
  ProfilePageWrapper,
} from "./Profile.styles";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { Profile } from "../../components/Profile/Profile";
import { ProfileInsurance } from "../../components/Profile/ProfileInsurance";
import { GetTypographyContent } from "../../components/shared/Typography/Typography";
import { ProfileResetEmail } from "../../components/Profile/ProfileResetEmail";
import { ProfileResetPassword } from "../../components/Profile/ProfileResetPassword";
import { getUserAuth } from "../../recoil/selectors";
import { patientProfileState } from "../../recoil/atoms";
import { GetPhiRequest } from "../../components/Profile/Services/UpdateProfileInfoService";
import {
  PhiInsuranceStatus,
  UserProfileAuthClientResponse,
} from "../../recoil/types";
import Loading from "../../components/shared/Loading/Loading";
import { ProfileNotifications } from "../../components/Profile/ProfileNotifications";
import { LanguageSwitcher } from "../../components/LanguageSwitcher/LanguageSwitcher";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";

const ProfilePage = (): React.ReactElement => {
  const [resetPassword, setResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState(false);
  const { deviceType } = useDeviceHook();
  const [patientInfo, setPatientInfo] = useRecoilState(patientProfileState);
  const authInfo = useRecoilValue(getUserAuth);
  const { t } = useTranslation();
  const { enabled: showLanguageSwitcher } = useBooleanFeatureFlag(
    FeatureFlags.PatientPortalSpanishLanguageToggle,
  );

  useEffect(() => {
    const getProfileInfo = async () => {
      const response = await GetPhiRequest(authInfo?.access_token);
      if (response?.ok) {
        const profile =
          (await response.json()) as UserProfileAuthClientResponse;
        setPatientInfo(profile);
      }
    };
    if (!patientInfo && authInfo) {
      getProfileInfo();
    }
  }, [patientInfo, authInfo]);

  return (
    <ProfilePageWrapper>
      {!patientInfo && authInfo && <Loading />}
      {patientInfo && (
        <ProfilePageComponentWrapper deviceType={deviceType}>
          <ProfilePageComponentGrid deviceType={deviceType}>
            {resetEmail && !resetPassword && (
              <ProfileResetEmail setResetEmail={setResetEmail} />
            )}
            {!resetEmail && resetPassword && (
              <ProfileResetPassword setResetPassword={setResetPassword} />
            )}
            {!resetEmail && !resetPassword && (
              <>
                <LandingGeneralContainer>
                  <Profile
                    setResetEmail={setResetEmail}
                    setResetPassword={setResetPassword}
                  />
                </LandingGeneralContainer>
                {showLanguageSwitcher && (
                  <LandingGeneralContainer>
                    {GetTypographyContent({
                      content: t("profile.languageSettings"),
                      size: "h3",
                      textColor: "greyMedium",
                      isBold: true,
                    })}
                    <LanguageSwitcher showLanguageSwitcher variant="settings" />
                  </LandingGeneralContainer>
                )}
                <div>
                  {patientInfo.phi.ins_status !== PhiInsuranceStatus.None && (
                    <LandingGeneralContainer>
                      <ProfileInsurance />
                    </LandingGeneralContainer>
                  )}
                </div>
              </>
            )}
            <ProfileContactWrapper>
              {GetTypographyContent({
                content: t("profile.contactPrompt.prefix"),
                size: "medium",
              })}
              {GetTypographyContent({
                content: t("profile.contactPrompt.link"),
                size: "medium",
                link: "./contactUs",
                textColor: "greenMedium",
              })}
            </ProfileContactWrapper>
            {!resetEmail && !resetPassword && (
              <LandingGeneralContainer style={{ marginTop: "80px" }}>
                <ProfileNotifications />
              </LandingGeneralContainer>
            )}
          </ProfilePageComponentGrid>
        </ProfilePageComponentWrapper>
      )}
    </ProfilePageWrapper>
  );
};

export default ProfilePage;
